import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { Calendar, Download } from 'lucide-react';
import ReportHeader from '@/components/ReportHeader';
import { apiService } from '@/services/api';
import { useFilialSelecionada } from '@/hooks/useFilialSelecionada';

const Container = styled.div`
  padding: 16px;
`;

const Filtros = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px;
`;

const Campo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 12px;
  color: #6b7280;
`;

const Input = styled.input`
  height: 36px;
  padding: 0 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
`;

const Select = styled.select`
  height: 36px;
  padding: 0 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
`;

const Acoes = styled.div`
  margin-left: auto;
  display: flex;
  gap: 8px;
`;

const Botao = styled.button`
  height: 36px;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #d1d5db;
  background: #f9fafb;
  color: #111827;
  border-radius: 6px;
  cursor: pointer;
  &:hover { background: #f3f4f6; }
  &:disabled { opacity: .6; cursor: not-allowed; }
`;

const Tabela = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

const Th = styled.th`
  text-align: left;
  padding: 10px;
  background: #f3f4f6;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 10px;
  border-bottom: 1px solid #f1f5f9;
`;

// estilos não utilizados removidos

const ScrollTop = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  height: 16px;
  margin: 0 16px 8px 16px;
`;

const ScrollBottom = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
`;

const RelatorioVendas: React.FC = () => {
  const { filialSelecionada } = useFilialSelecionada();
  const [nc, setNc] = useState<string>('');
  const [produto, setProduto] = useState<string>('');
  const [dataInicio, setDataInicio] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [dataFim, setDataFim] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [formaPagamento, setFormaPagamento] = useState<'todos' | 'avista' | 'aprazo'>('todos');
  const [grupo, setGrupo] = useState<string>('todos');
  const [grupos, setGrupos] = useState<Array<{ Controle: number; Grupo: string }>>([]);
  const topScrollRef = useRef<HTMLDivElement | null>(null);
  const bottomScrollRef = useRef<HTMLDivElement | null>(null);
  const [contentWidth, setContentWidth] = useState<number>(0);

  const formatarDataBR = (valor: any): string => {
    if (!valor) return '';
    const s = String(valor);
    const match = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const [, ano, mes, dia] = match;
      return `${dia}/${mes}/${ano}`;
    }
    const d = new Date(s);
    if (!isNaN(d.getTime())) return d.toLocaleDateString('pt-BR');
    return s;
  };

  const colunasDecimais = useMemo(() => new Set([
    'ValorUnitarioProduto',
    'ValorCobradoProdutoUnitario',
    'VlrDesconto',
    'PrecoComTaxaCorreta'
  ]), []);

  const formatarNumero2Casas = (valor: any): string => {
    if (valor === null || valor === undefined || valor === '') return (0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const num = typeof valor === 'number' ? valor : parseFloat(String(valor).replace(',', '.'));
    if (isNaN(num)) return String(valor);
    return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const queryKey = useMemo(() => (
    ['relatorio-vendas', filialSelecionada?.id, nc, produto, dataInicio, dataFim, formaPagamento, grupo]
  ), [filialSelecionada?.id, nc, produto, dataInicio, dataFim, formaPagamento, grupo]);

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey,
    enabled: !!filialSelecionada?.id,
    queryFn: async () => {
      if (!filialSelecionada?.id) return null;
      const res = await apiService.obterRelatorioVendas(
        filialSelecionada.id,
        {
          NC: (nc && /^\d+$/.test(nc) ? nc : undefined),
          produto: (produto && produto.trim() !== '' ? produto : undefined),
          dataInicio,
          dataFim,
          formaPagamento,
          grupo
        }
      );
      return res?.data;
    }
  });

  useEffect(() => {
    const carregar = async () => {
      if (!filialSelecionada?.id) return;
      try {
        const lista = await apiService.listarGrupos(filialSelecionada.id);
        setGrupos(lista);
      } catch (e) {
        // silencioso, já há toasts globais no service
      }
    };
    carregar();
  }, [filialSelecionada?.id]);

  const colunas: string[] = data?.colunas || [
    'formaDePagamento','NC','data','Controle','Produto','Quantidade',
    'ValorUnitarioProduto','ValorCobradoProdutoUnitario','VlrDesconto','Cliente','PrecoComTaxaCorreta'
  ];

  const linhas: any[] = data?.dados || [];

  const headerLabels = useMemo<Record<string, string>>(() => ({
    formaDePagamento: 'Forma de Pagamento',
    Grupo: 'Grupo',
    ValorUnitarioProduto: 'Valor Unit. Prod.',
    ValorCobradoProdutoUnitario: 'Valor Cobrado',
    VlrDesconto: 'Valor Desconto',
    PrecoComTaxaCorreta: 'Valor Correto'
  }), []);

  useEffect(() => {
    const bottom = bottomScrollRef.current;
    if (!bottom) return;
    const update = () => setContentWidth(bottom.scrollWidth);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [linhas, colunas]);

  const handleTopScroll = () => {
    const top = topScrollRef.current;
    const bottom = bottomScrollRef.current;
    if (top && bottom && bottom.scrollLeft !== top.scrollLeft) {
      bottom.scrollLeft = top.scrollLeft;
    }
  };

  const handleBottomScroll = () => {
    const top = topScrollRef.current;
    const bottom = bottomScrollRef.current;
    if (top && bottom && top.scrollLeft !== bottom.scrollLeft) {
      top.scrollLeft = bottom.scrollLeft;
    }
  };

  const handleExportar = () => {
    if (!colunas || colunas.length === 0) return;
    const sep = ';';

    const toCsvValue = (coluna: string, valor: any): string => {
      const raw = coluna === 'data'
        ? formatarDataBR(valor)
        : (colunasDecimais.has(coluna) ? formatarNumero2Casas(valor) : String(valor ?? ''));
      const needsQuote = raw.includes('"') || raw.includes(sep) || /[\n\r]/.test(raw);
      const escaped = raw.replace(/"/g, '""');
      return needsQuote ? `"${escaped}"` : escaped;
    };

    const header = colunas.join(sep);
    const body = linhas.map((linha) => colunas.map((c) => toCsvValue(c, linha[c])).join(sep)).join('\n');

    const filialNome = filialSelecionada?.nome?.replace(/[^\w\-\s]/g, '') || 'filial';
    const nomeArquivo = `relatorio_vendas_${filialNome}_${dataInicio}_a_${dataFim}.csv`;

    const csvContent = `\ufeff${header}\n${body}`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = nomeArquivo;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Container>
      <ReportHeader
        title="Relatório de recebimentos por NC"
        subtitle={`${filialSelecionada?.nome || ''} • ${data?.totalRegistros ?? 0} registros`}
        rightActions={(
          <Acoes>
            <Botao onClick={handleExportar} disabled={isLoading || isFetching || (linhas?.length ?? 0) === 0}>
              <Download size={16} /> Exportar
            </Botao>
          </Acoes>
        )}
      />

      <Filtros>
        <Campo style={{ width: 160 }}>
          <Label>NC</Label>
          <Input
            type="text"
            inputMode="numeric"
            placeholder="Digite o NC"
            value={nc}
            onChange={(e) => {
              const onlyDigits = e.target.value.replace(/\D/g, '');
              setNc(onlyDigits);
            }}
          />
        </Campo>
        <Campo style={{ minWidth: 260, flex: 1 }}>
          <Label>Produto</Label>
          <Input
            type="text"
            placeholder="Nome do produto ou código (parcial)"
            value={produto}
            onChange={(e) => setProduto(e.target.value)}
          />
        </Campo>
      </Filtros>

      <Filtros>
        <Campo>
          <Label>Data Início</Label>
          <Input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)} />
        </Campo>
        <Campo>
          <Label>Data Fim</Label>
          <Input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} />
        </Campo>
        <Campo>
          <Label>Forma de Pagamento</Label>
          <Select value={formaPagamento} onChange={e => setFormaPagamento(e.target.value as 'todos' | 'avista' | 'aprazo')}>
            <option value="todos">Todos</option>
            <option value="avista">À vista</option>
            <option value="aprazo">À prazo</option>
          </Select>
        </Campo>
        <Campo>
          <Label>Grupo do Produto</Label>
          <Select value={grupo} onChange={e => setGrupo(e.target.value)}>
            <option value="todos">Todos</option>
            {grupos.map(g => (
              <option key={g.Controle} value={String(g.Controle)}>{g.Grupo}</option>
            ))}
          </Select>
        </Campo>
        <Acoes>
          <Botao onClick={() => refetch()} disabled={isLoading || isFetching}>
            <Calendar size={16} /> Aplicar
          </Botao>
        </Acoes>
      </Filtros>

      <ScrollTop ref={topScrollRef} onScroll={handleTopScroll}>
        <div style={{ width: contentWidth }} />
      </ScrollTop>

      <ScrollBottom ref={bottomScrollRef} onScroll={handleBottomScroll}>
        <Tabela>
          <thead>
            <tr>
              {colunas.map((c) => (
                <Th key={c}>{headerLabels[c] ?? c}</Th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><Td colSpan={colunas.length}>Carregando...</Td></tr>
            ) : linhas.length === 0 ? (
              <tr><Td colSpan={colunas.length}>Nenhum registro encontrado</Td></tr>
            ) : (
              linhas.map((linha, idx) => (
                <tr key={idx}>
                  {colunas.map((c) => (
                    <Td key={c}>
                      {c === 'data'
                        ? formatarDataBR(linha[c])
                        : colunasDecimais.has(c)
                          ? formatarNumero2Casas(linha[c])
                          : String(linha[c] ?? '')}
                    </Td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </Tabela>
      </ScrollBottom>
    </Container>
  );
};

export default RelatorioVendas;


